import { useEffect, useRef, useState } from 'react';
import { useNav } from '../stores/navigation';
import { useUnlocked } from '../stores/unlocked';
import { QRCameraScanner } from '../qr/scanner';
import { parseARQr, failureMessage } from '../qr/parser';

export function ScannerScreen() {
  const go = useNav((s) => s.go);
  const unlock = useUnlocked((s) => s.unlock);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QRCameraScanner | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualText, setManualText] = useState('');
  const [status, setStatus] = useState<'starting' | 'scanning' | 'failed'>('starting');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const scanner = new QRCameraScanner();
    scannerRef.current = scanner;

    scanner
      .start(video, (text) => {
        handleText(text);
      })
      .then(() => setStatus('scanning'))
      .catch((err) => {
        console.error('[Scanner] start failed', err);
        setStatus('failed');
        setError('カメラを開けませんでした。手入力で試してください。');
      });

    return () => {
      scanner.stop();
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleText(text: string) {
    const parsed = parseARQr(text);
    if (!parsed.ok) {
      setError(failureMessage(parsed.reason));
      return;
    }
    const result = unlock(parsed.params);
    scannerRef.current?.stop();
    go({ kind: 'detail', entry: { ...parsed.params, unlockedAt: new Date().toISOString() } });
    if (result === 'existing') {
      // 既存だった旨を一瞬だけ通知できると親切（MVP では省略）
    }
  }

  return (
    <div className="screen">
      <button className="btn btn-secondary" onClick={() => go({ kind: 'home' })} style={{ alignSelf: 'flex-start' }}>
        ← 戻る
      </button>
      <h2 className="screen-title">QR をスキャン</h2>

      <div
        style={{
          position: 'relative',
          width: 'min(420px, 88vw)',
          aspectRatio: '1 / 1',
          background: '#000',
          borderRadius: 16,
          overflow: 'hidden',
          margin: '0 auto',
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {status !== 'scanning' && (
          <div
            className="center"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
            }}
          >
            <span className="muted">{status === 'starting' ? 'カメラ起動中...' : 'カメラ未対応'}</span>
          </div>
        )}
      </div>

      {error && <p className="error" style={{ textAlign: 'center', marginTop: 16 }}>{error}</p>}

      <details className="card" style={{ marginTop: 'auto' }}>
        <summary className="muted">QR テキストを手入力</summary>
        <div className="stack" style={{ marginTop: 12 }}>
          <input
            type="text"
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder="msqr:v2:..."
            style={{
              width: '100%',
              padding: 10,
              borderRadius: 8,
              border: '1px solid #2a2a44',
              background: '#0e0e1c',
              color: 'var(--fg)',
              fontFamily: 'monospace',
            }}
          />
          <button className="btn" onClick={() => handleText(manualText.trim())}>
            読み込む
          </button>
        </div>
      </details>
    </div>
  );
}
